// ── The page wishlist ("shopping list") ───────────────────────────────
//
// Things a visitor wants to come back to, on a PUBLISHED page with no
// session and no server — the cart's sibling, built exactly the same way
// (see cart.ts). It is a LIST, never a price authority: "Move to cart"
// puts the entry in the basket, and the order is priced again from the
// saved record server-side like every other cart line.
//
// ONE implementation, two callers: the React surfaces import these
// functions, the static site gets the same `initWishlist` serialised by
// `wishlistScript()`. Everything inside `initWishlist` must therefore stay
// closure-free and dependency-free — `Function.prototype.toString` is what
// ships it.
//
// Storage: `pg_wishlist` (+ `:<scope>`), scoped by the SAME global the cart
// uses (`__PG_CART_SCOPE__`), so the two lists of one application always
// travel together and two applications sharing an origin stay apart.

export interface WishlistItem {
  sku: string
  name: string
  /** Numeric price, for display only. */
  price: number
  /** What precedes the number when shown — '₹', '$', '€'. */
  prefix: string
  image?: string
  href?: string
  meta?: string
  /** When it was saved (ms since epoch) — newest first on the list. */
  addedAt?: number
}

export function wishlistKey(): string {
  const scope = (typeof window !== 'undefined'
    && (window as unknown as { __PG_CART_SCOPE__?: string }).__PG_CART_SCOPE__) || ''
  return 'pg_wishlist' + (scope ? ':' + scope : '')
}

export function readWishlist(): WishlistItem[] {
  try {
    const raw = window.localStorage.getItem(wishlistKey())
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    // Browser storage a visitor can edit: defend every field.
    const out: WishlistItem[] = []
    const seen: Record<string, true> = {}
    for (let i = 0; i < parsed.length; i++) {
      const w = parsed[i]
      if (!w || typeof w !== 'object' || !w.sku) continue
      const sku = String(w.sku)
      if (seen[sku]) continue
      seen[sku] = true
      const price = Number(w.price)
      out.push({
        sku, name: String(w.name || sku),
        price: isFinite(price) && price >= 0 ? price : 0,
        prefix: String(w.prefix || ''),
        image: w.image ? String(w.image) : undefined,
        href: w.href ? String(w.href) : undefined,
        meta: w.meta ? String(w.meta) : undefined,
        addedAt: Number(w.addedAt) || undefined,
      })
    }
    return out
  } catch {
    return []
  }
}

export function writeWishlist(items: WishlistItem[]): void {
  try {
    window.localStorage.setItem(wishlistKey(), JSON.stringify(items))
  } catch { /* storage unavailable: the list lives for this page view only */ }
  try {
    window.dispatchEvent(new CustomEvent('pg:wishlist', { detail: { items } }))
  } catch { /* older browsers */ }
}

export function isInWishlist(sku: string): boolean {
  const k = String(sku || '')
  return !!k && readWishlist().some((w) => w.sku === k)
}

/** Add the item when absent, remove it when present. Returns the new state. */
export function toggleWishlist(item: Omit<WishlistItem, 'addedAt'>): boolean {
  const items = readWishlist()
  const sku = String(item.sku || '')
  if (!sku) return false
  const at = items.findIndex((w) => w.sku === sku)
  if (at >= 0) {
    items.splice(at, 1)
    writeWishlist(items)
    return false
  }
  items.unshift({
    sku, name: String(item.name || sku), price: Number(item.price) || 0,
    prefix: String(item.prefix || ''), image: item.image, href: item.href,
    meta: item.meta, addedAt: Date.now(),
  })
  writeWishlist(items)
  return true
}

export function removeFromWishlist(sku: string): WishlistItem[] {
  const items = readWishlist().filter((w) => w.sku !== String(sku))
  writeWishlist(items)
  return items
}

// ── DOM wiring ─────────────────────────────────────────────────────────
//
// The blocks render a SHELL (with hidden row templates); this fills it.
//
//   [data-pg-wish-toggle]='{"sku",…}'   a heart: toggles the entry
//     [data-pg-wish-label]              its label span, swapped between
//                                       data-pg-wish-add / data-pg-wish-remove
//   [data-pg-wish-count]                a badge count
//   [data-pg-wish-lines]                a list host with
//     [data-pg-wish-row-template] / [data-pg-wish-list] / [data-pg-wish-empty]
//     row parts: [data-pg-wish-image] [data-pg-wish-name] [data-pg-wish-meta]
//                [data-pg-wish-price] [data-pg-wish-move] [data-pg-wish-remove]

export function initWishlist(): void {
  const doc = document
  const KEY_BASE = 'pg_wishlist'
  const scopeOf = (): string =>
    (window as unknown as { __PG_CART_SCOPE__?: string }).__PG_CART_SCOPE__ || ''

  const read = (): WishlistItem[] => {
    try {
      const scope = scopeOf()
      const raw = window.localStorage.getItem(KEY_BASE + (scope ? ':' + scope : ''))
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed.filter((w: WishlistItem) => w && w.sku) : []
    } catch { return [] }
  }
  const write = (items: WishlistItem[]) => {
    try {
      const scope = scopeOf()
      window.localStorage.setItem(KEY_BASE + (scope ? ':' + scope : ''), JSON.stringify(items))
    } catch { /* storage blocked */ }
    try { window.dispatchEvent(new CustomEvent('pg:wishlist', { detail: { items } })) } catch { /* old browser */ }
  }
  // "Move to cart" writes the CART's store, in the cart's own shape, and
  // announces it on `pg:cart` so every cart block re-renders. Mirrors
  // cart.ts `addToCart` — this function cannot import it.
  const addToCartStore = (w: WishlistItem) => {
    try {
      const scope = scopeOf()
      const key = 'pg_cart' + (scope ? ':' + scope : '')
      const raw = window.localStorage.getItem(key)
      const parsed = raw ? JSON.parse(raw) : []
      const lines = Array.isArray(parsed) ? parsed.filter((l: { sku?: string }) => l && l.sku) : []
      let found = false
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i]
        const k = l.key ? String(l.key) : (l.start ? String(l.sku) + '|' + l.start + '|' + (l.end || '') : String(l.sku))
        if (k === String(w.sku)) { l.qty = Math.min((Number(l.qty) || 0) + 1, 9999); found = true; break }
      }
      if (!found) {
        lines.push({
          sku: String(w.sku), name: String(w.name || w.sku), price: Number(w.price) || 0,
          prefix: String(w.prefix || ''), image: w.image, href: w.href, meta: w.meta,
          qty: 1, key: String(w.sku),
        })
      }
      window.localStorage.setItem(key, JSON.stringify(lines))
      try { window.dispatchEvent(new CustomEvent('pg:cart', { detail: { lines } })) } catch { /* old browser */ }
    } catch { /* storage blocked */ }
  }
  const money = (n: number, prefix: string): string => {
    const v = Math.round(Number(n) || 0)
    if (!v && !prefix) return ''
    const s = String(Math.abs(v))
    let grouped: string
    if (prefix.indexOf('₹') !== -1) {
      if (s.length <= 3) grouped = s
      else {
        let head = s.slice(0, -3); const tail = s.slice(-3); const parts: string[] = []
        while (head.length > 2) { parts.unshift(head.slice(-2)); head = head.slice(0, -2) }
        if (head) parts.unshift(head)
        grouped = parts.join(',') + ',' + tail
      }
    } else {
      grouped = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    return (v < 0 ? '-' : '') + prefix + grouped
  }

  const render = () => {
    const items = read()
    const has: Record<string, true> = {}
    for (let i = 0; i < items.length; i++) has[String(items[i].sku)] = true

    // hearts
    const toggles = doc.querySelectorAll('[data-pg-wish-toggle]')
    for (let i = 0; i < toggles.length; i++) {
      const t = toggles[i] as HTMLElement
      let sku = ''
      try { sku = String((JSON.parse(t.getAttribute('data-pg-wish-toggle') || '{}') || {}).sku || '') } catch { /* skip */ }
      const on = !!sku && !!has[sku]
      if (on) t.setAttribute('data-pg-wished', '')
      else t.removeAttribute('data-pg-wished')
      t.setAttribute('aria-pressed', on ? 'true' : 'false')
      const label = (on ? t.getAttribute('data-pg-wish-remove') : t.getAttribute('data-pg-wish-add')) || ''
      if (label) {
        t.setAttribute('aria-label', label)
        t.setAttribute('title', label)
      }
      const span = t.querySelector('[data-pg-wish-label]') as HTMLElement | null
      if (span && label) span.textContent = label
    }

    // badges
    const badges = doc.querySelectorAll('[data-pg-wish-count]')
    for (let i = 0; i < badges.length; i++) {
      const b = badges[i] as HTMLElement
      b.textContent = String(items.length)
      if (b.hasAttribute('data-pg-wish-hide-empty')) {
        b.style.display = items.length === 0 ? 'none' : 'inline-flex'
      }
    }

    // lists
    const hosts = doc.querySelectorAll('[data-pg-wish-lines]')
    for (let h = 0; h < hosts.length; h++) {
      const host = hosts[h] as HTMLElement
      const tpl = host.querySelector('[data-pg-wish-row-template]') as HTMLElement | null
      const list = host.querySelector('[data-pg-wish-list]') as HTMLElement | null
      const empty = host.querySelector('[data-pg-wish-empty]') as HTMLElement | null
      if (!tpl || !list) continue
      tpl.style.display = 'none'
      list.innerHTML = ''
      for (let i = 0; i < items.length; i++) {
        const w = items[i]
        const row = tpl.cloneNode(true) as HTMLElement
        row.removeAttribute('data-pg-wish-row-template')
        row.setAttribute('data-pg-wish-row', String(w.sku))
        // Name the layout: the template's display is inline, so clearing
        // `none` would leave a block and stack every cell (see cart.ts).
        row.style.display = 'flex'
        const img = row.querySelector('[data-pg-wish-image]') as HTMLImageElement | null
        if (img) {
          if (w.image) { img.src = w.image; img.alt = String(w.name || ''); img.style.display = 'block' }
          else img.style.display = 'none'
        }
        const nameEl = row.querySelector('[data-pg-wish-name]') as HTMLElement | null
        if (nameEl) {
          nameEl.textContent = String(w.name || w.sku)
          if (w.href && nameEl.tagName === 'A') (nameEl as HTMLAnchorElement).href = w.href
        }
        const metaEl = row.querySelector('[data-pg-wish-meta]') as HTMLElement | null
        if (metaEl) {
          if (w.meta) { metaEl.textContent = w.meta; metaEl.style.display = '' }
          else metaEl.style.display = 'none'
        }
        const priceEl = row.querySelector('[data-pg-wish-price]') as HTMLElement | null
        if (priceEl) priceEl.textContent = Number(w.price) ? money(Number(w.price), String(w.prefix || '')) : ''
        const mv = row.querySelector('[data-pg-wish-move]')
        if (mv) mv.setAttribute('data-pg-wish-sku', String(w.sku))
        const rm = row.querySelector('[data-pg-wish-remove]')
        if (rm) rm.setAttribute('data-pg-wish-sku', String(w.sku))
        list.appendChild(row)
      }
      if (empty) {
        if (items.length === 0) { empty.removeAttribute('hidden'); empty.style.display = 'flex' }
        else { empty.setAttribute('hidden', ''); empty.style.display = 'none' }
      }
    }
  }

  if (!(window as unknown as { __pgWishWired?: boolean }).__pgWishWired) {
    ;(window as unknown as { __pgWishWired?: boolean }).__pgWishWired = true

    doc.addEventListener('click', (e) => {
      let t = e.target as HTMLElement | null
      const hit = (n: HTMLElement) => !!n.hasAttribute && (
        n.hasAttribute('data-pg-wish-toggle') || n.hasAttribute('data-pg-wish-move')
        || n.hasAttribute('data-pg-wish-remove'))
      while (t && t !== doc.body && !hit(t)) t = t.parentElement
      if (!t || t === doc.body) return
      e.preventDefault()
      // A heart sits ON a card that may be a link: never navigate.
      try { e.stopPropagation() } catch { /* ignore */ }

      if (t.hasAttribute('data-pg-wish-toggle')) {
        let p: Partial<WishlistItem> = {}
        try { p = JSON.parse(t.getAttribute('data-pg-wish-toggle') || '{}') || {} } catch { return }
        const sku = String(p.sku || '')
        if (!sku) return
        const items = read()
        let at = -1
        for (let i = 0; i < items.length; i++) if (String(items[i].sku) === sku) { at = i; break }
        if (at >= 0) items.splice(at, 1)
        else {
          items.unshift({
            sku, name: String(p.name || sku), price: Number(p.price) || 0,
            prefix: String(p.prefix || ''), image: p.image, href: p.href, meta: p.meta,
            addedAt: Date.now(),
          })
        }
        write(items)
        render()
        return
      }

      const sku = t.getAttribute('data-pg-wish-sku') || ''
      if (!sku) return
      const items = read()
      if (t.hasAttribute('data-pg-wish-move')) {
        for (let i = 0; i < items.length; i++) {
          if (String(items[i].sku) === sku) { addToCartStore(items[i]); break }
        }
        const host = t.closest ? t.closest('[data-pg-wish-lines]') : null
        if (host && host.hasAttribute('data-pg-wish-keep')) { render(); return }
      }
      write(items.filter((w) => String(w.sku) !== sku))
      render()
    })

    // Another tab, or another block on this page, changed the list.
    window.addEventListener('storage', render)
    window.addEventListener('pg:wishlist', render)
  }

  render()
}

/** The same wiring, for a page with no React on it. */
export function wishlistScript(): string {
  return `(function(){
  var init=${initWishlist.toString()};
  function go(){try{init()}catch(e){}}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',go);else go();
})();`
}
